'use client';
import Image from 'next/image';

import { Modal } from '../../../ui';
import { toTitleCase } from '../../../utils';
import { Confirmation } from './Confirmation';
import {
  SupplierRequestWizardContextProvider,
  useSupplierRequestWizardContext,
} from './context';
import { Initial } from './Initial';
import { Preferences } from './Preferences';
import { Requirements } from './Requirements';
import { Review } from './Review';
import { SupplierRequestStep } from './types';

const sliderSteps: SupplierRequestStep[] = [
  'REQUIREMENTS',
  'PREFERENCES',
  'REVIEW',
];

export * from './DocumentUpload';

const SupplierRequestModalInner = ({
  showModal,
  setShowModal,
}: {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
}) => {
  const {
    currentStep,
    flow,
    setCurrentStep,
    setSupplierRequestInput,
    supplierRequestInput,
  } = useSupplierRequestWizardContext();

  const handleCloseModal = () => {
    setCurrentStep('INITIAL');
    setSupplierRequestInput({
      projectName: '',
      description: '',
      timeframe: '',
      budget: '',
      unitVolume: '',
      locations: '',
      questions: [],
      documents: [],
      supplierPreference: [],
      databasePreference: undefined,
      creatorName: '',
      creatorTitle: '',
      category: '',
      subCategory: '',
    });
    setShowModal(false);
  };

  const getStepsIcon = (value: number) => {
    const stepNumber = sliderSteps.findIndex((step) => step === currentStep);
    if (stepNumber === value) {
      return 'base';
    }

    if (value > stepNumber) {
      return 'disabled';
    }

    return 'completed';
  };

  const getTitle = () => {
    switch (currentStep) {
      case 'CONFIRMATION':
        return 'Thank You for Submitting Your Supplier Request!';
      case 'INITIAL':
        return 'Create A Supplier Request';
      default:
        return 'Supplier Request Wizard';
    }
  };

  const getIcon = () => {
    switch (currentStep) {
      case 'INITIAL':
        return '/icons/connections.svg';
      case 'CONFIRMATION':
        return '/icons/rocket.svg';
      default:
        return '/icons/sparkles.svg';
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'INITIAL':
        return <Initial />;
      case 'REQUIREMENTS':
        return <Requirements />;
      case 'PREFERENCES':
        return <Preferences />;
      case 'REVIEW':
        return <Review />;
      case 'CONFIRMATION':
        return <Confirmation handleCloseModal={handleCloseModal} />;
      default:
        throw new Error('unexpected step name ' + currentStep);
    }
  };

  return (
    <>
      <Modal
        title={getTitle()}
        icon={getIcon()}
        confirmation={currentStep === 'CONFIRMATION'}
        showModal={showModal}
        handleCloseModal={handleCloseModal}
        extra={
          !['CONFIRMATION', 'INITIAL'].includes(currentStep) && (
            <ul className="flex items-center w-full text-xs font-medium sm:text-base border-b border-gray-200 py-5 px-9">
              {sliderSteps.map((title, index) => (
                <li
                  key={index}
                  className={`hover:after:transition-all transition-all ease-out duration-1000 flex items-center justify-center w-full relative after:content-['']  after:w-full after:h-1  ${index >= sliderSteps.findIndex((value) => value === currentStep) ? 'after:bg-gray-200' : 'after:bg-primary-500'}  after:inline-block after:absolute after:top-5 after:left-[50%] [&:last-child]:after:w-0`}
                >
                  <div className="flex items-center	justify-center flex-col gap-[12px] text-xs">
                    <Image
                      src={`/icons/step-icon-${getStepsIcon(index)}.svg`}
                      className="mt-[9px] z-20"
                      alt="step icon"
                      width={24}
                      height={24}
                    />
                    {toTitleCase(title)}
                  </div>
                </li>
              ))}
            </ul>
          )
        }
      >
        {renderStep()}
      </Modal>
    </>
  );
};

export const SupplierRequestModal = ({
  showModal,
  setShowModal,
}: {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
}) => {
  return (
    <SupplierRequestWizardContextProvider>
      <SupplierRequestModalInner
        showModal={showModal}
        setShowModal={setShowModal}
      />
    </SupplierRequestWizardContextProvider>
  );
};
